import { Controller } from "@hotwired/stimulus"

// Copies a value to the clipboard and confirms it in place.
//
// Exists because mailto: and tel: links do nothing on a machine with no mail
// client or dialler configured, and the address inside a link is awkward to
// select by hand — nearly impossible on a phone. The copy button is the fallback
// that always works.
//
// Markup:
//   <span data-controller="copy-to-clipboard" data-copy-to-clipboard-value-value="a@b.com">
//     <button data-action="click->copy-to-clipboard#copy"
//             data-copy-to-clipboard-target="button">Copy</button>
//   </span>
export default class extends Controller {
  static targets = ["button"]
  static values = {
    value: String,
    label: { type: String, default: "Copy" },
    confirmed: { type: String, default: "Copied" }
  }

  async copy(event) {
    event.preventDefault()

    if (await this.write()) {
      this.confirm()
    } else {
      // Last resort: surface the value so it can be copied by hand rather than
      // failing silently.
      window.prompt("Copy this address", this.valueValue)
    }
  }

  async write() {
    // navigator.clipboard needs a secure context, so it is absent on plain http.
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(this.valueValue)
        return true
      } catch {
        // fall through to the textarea approach
      }
    }

    const field = document.createElement("textarea")
    field.value = this.valueValue
    field.setAttribute("readonly", "")
    field.style.position = "fixed"
    field.style.opacity = "0"
    document.body.appendChild(field)
    field.select()

    let copied = false
    try {
      copied = document.execCommand("copy")
    } catch {
      copied = false
    }
    document.body.removeChild(field)
    return copied
  }

  confirm() {
    if (!this.hasButtonTarget) return

    clearTimeout(this.resetTimer)
    this.buttonTarget.textContent = this.confirmedValue
    this.buttonTarget.dataset.copied = "true"

    this.resetTimer = setTimeout(() => {
      this.buttonTarget.textContent = this.labelValue
      delete this.buttonTarget.dataset.copied
    }, 2000)
  }

  disconnect() {
    clearTimeout(this.resetTimer)
  }
}
